import type { ResponsePrepTime } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../useDevice";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../AudioPlayerContainer";
import Box from "../../../Box";
import usePrepSequence from "../../hooks/usePrepSequence";
import WordSelection from "../WordSelection";
import { ItemInstruction } from "./pte/ItemInstruction";
import { PrepCountdownBanner } from "./pte/PrepCountdownBanner";
import { SubInstruction } from "./pte/SubInstruction";

type Props = {
	instruction: string;
	subInstruction?: string;
	audioUri: string;
	transcriptText: string;
	selectedChoiceIds: string[];
	correctAnswerIds?: string[];
	isAnswered?: boolean;
	inReviewMode?: boolean;
	disabled?: boolean;
	onToggleWord: (wordId: string) => void;
	prepTime?: ResponsePrepTime;
	allowAudioReplay?: boolean;
	onAudioEnded?: () => void;
};

const HighlightIncorrectWords = ({
	instruction,
	subInstruction,
	audioUri,
	transcriptText,
	selectedChoiceIds,
	correctAnswerIds,
	isAnswered,
	inReviewMode,
	disabled,
	onToggleWord,
	prepTime,
	allowAudioReplay,
	onAudioEnded,
}: Props) => {
	const { isMobile } = useDevice();
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const isTimed = prepTime != null;
	const playOnce = !allowAudioReplay;

	const { remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		hasAudioStimulus: Boolean(audioUri),
		hasRecorder: false,
		audioPlayerRef,
		onAudioEnded,
		enabled: isTimed && !isAnswered,
	});

	return (
		<Box gap={isMobile ? 16 : 24}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{showBanner ? (
					<PrepCountdownBanner
						remainingSeconds={remainingPrepSeconds}
						playOnce={playOnce}
					/>
				) : null}
				<AudioPlayerContainer
					ref={audioPlayerRef}
					uri={audioUri}
					playOnce={playOnce}
				/>
			</Box>
			<WordSelection
				transcriptText={transcriptText}
				selectedChoiceIds={selectedChoiceIds}
				correctAnswerIds={correctAnswerIds}
				isAnswered={isAnswered}
				inReviewMode={inReviewMode}
				disabled={disabled}
				onToggleWord={onToggleWord}
			/>
		</Box>
	);
};

export default HighlightIncorrectWords;
